import React, { useState, useEffect, useCallback, useMemo } from "react";
import { keyframes } from "@mui/system";
import { useDebounce } from "../hooks/useDebounce";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Avatar,
  alpha,
  useTheme,
  Skeleton,
  Tooltip,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import {
  Add,
  Search,
  MoreVert,
  Edit,
  Delete,
  Lock,
  LockOpen,
  Refresh,
  Block,
  Close,
  CheckCircle,
  Warning,
  BadgeOutlined,
  WorkOutline,
  GroupAdd,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSnackbar } from "notistack";
import { userService } from "../services/userService";
import { centerService } from "../services/centerService";
import { roleService } from "../services/roleService";
import { User, UserRole, Center } from "../types";
import { useAuth } from "../context/AuthContext";
import { useModulePermissions } from "../hooks/useModulePermissions";
import ConfirmationDialog from "../components/common/ConfirmationDialog";
import BulkUploadEmployeesDialog from "../components/employees/BulkUploadEmployeesDialog";
import { format } from "date-fns";
import { getHashedColorPair } from '../utils/colorUtils';

// Validation schema - userCode will be auto-generated for all users
const userFormSchema = yup.object({
  name: yup.string().required("Name is required"),
  password: yup.string(),
  role: yup.string(),
  roleId: yup.string(),
  centerId: yup.string(),
});

const resetPasswordSchema = yup.object({
  newPassword: yup.string().required("New password is required").min(6, "Password must be at least 6 characters"),
  confirmPassword: yup
    .string()
    .required("Please confirm your password")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

// Same shimmer sweep used across the admissions module — non-blocking
// refresh indicator that keeps existing rows on screen while the next
// search / filter / page loads.
const tableShimmerKf = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
`;

// Color palette for roles - uses shared utility from colorUtils for consistent hashing

// Get consistent color for a role based on its name
const getRoleColor = (roleName: string, isDark: boolean) => {
  return getHashedColorPair(roleName, isDark);
};


const EmployeesPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user, decodedToken, loading: authLoading } = useAuth();
  const { canRead, canWrite } = useModulePermissions();

  // State
  const [users, setUsers] = useState<User[]>([]);
  const [centers, setCenters] = useState<Center[]>([]);
  const [roles, setRoles] = useState<Array<{ _id: string; name: string; description?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true); // Track initial load vs subsequent searches
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [userToToggleStatus, setUserToToggleStatus] = useState<User | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500); // Debounce to prevent API spam
  const [roleFilter, setRoleFilter] = useState<string>("");
  // School filter — super-admin only. Selecting a school scopes both the
  // employees list query AND the Role dropdown so the user only sees roles
  // belonging to that school.
  const [schoolFilter, setSchoolFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Check if user is school admin
  const isSchoolAdmin = useMemo(() =>
    decodedToken?.isSchoolAdmin === true || (!user?.isSystemAdmin && user?.schoolId),
    [decodedToken, user]
  );

  // Row-level guard: is the target employee the School Admin (owner)?
  // Only the Name is editable for school-scoped callers; Deactivate /
  // Reset Password / Delete are hidden. System Admins (platform owner)
  // bypass this — they can do anything to any employee. BE enforces the
  // same bypass on every write endpoint.
  const isSchoolAdminEmployee = useCallback(
    (u: User | null | undefined): boolean =>
      Boolean(u?.roleId && (u.roleId as any).isSchoolAdmin === true),
    []
  );
  const lockSchoolAdminRow    = !user?.isSystemAdmin;
  const editingIsSchoolAdmin  = lockSchoolAdminRow && isSchoolAdminEmployee(editingUser);
  const selectedIsSchoolAdmin = lockSchoolAdminRow && isSchoolAdminEmployee(selectedUser);

  // Form handling
  const formik = useFormik<any>({
    initialValues: {
      name: "",
      password: "",
      role: "",
      roleId: "",
      centerId: "",
    },
    validationSchema: userFormSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // Validate password for new users
        if (!editingUser && !values.password) {
          enqueueSnackbar("Password is required for new employees", { variant: "error" });
          return;
        }

        // Validate role selection
        // Both paths now use roleId (real Role ObjectId from the school's
        // role set). The legacy `role` enum is no longer used in this form.
        if (!values.roleId) {
          enqueueSnackbar("Please select a role", { variant: "error" });
          return;
        }
        if (!isSchoolAdmin && !values.centerId) {
          enqueueSnackbar("Please select a school", { variant: "error" });
          return;
        }
        
        if (isSchoolAdmin) {
          // Identity-only payload. Subject-teaching assignments for teachers
          // are owned by the Teaching Loads module and are managed there.
          const userData: any = {
            name: values.name,
            roleId: values.roleId,
          };

          if (!editingUser) {
            userData.password = values.password;
          }

          if (editingUser) {
            await roleService.updateUser(editingUser._id, userData);
            enqueueSnackbar("Employee updated successfully", { variant: "success" });
          } else {
            await roleService.createUser(userData);
            enqueueSnackbar("Employee created successfully", { variant: "success" });
          }
        } else {
          // Super-admin path. The user-controller maps the body's `role`
          // field to `roleId` so we send the real Role ObjectId here, not
          // the legacy UserRole enum string.
          let submitValues: any = {
            name: values.name,
            role: values.roleId,
            centerId: values.centerId || undefined,
          };

          if (!editingUser) {
            submitValues.password = values.password;
          }

          if (editingUser) {
            await userService.updateUser(editingUser._id, submitValues);
            enqueueSnackbar("Employee updated successfully", { variant: "success" });
          } else {
            await userService.createUser(submitValues);
            enqueueSnackbar("Employee created successfully", { variant: "success" });
          }
        }
        
        handleDialogClose();
        loadUsers();
      } catch (error: any) {
        enqueueSnackbar(error.message || "Operation failed", { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Password reset form
  const resetPasswordFormik = useFormik({
    initialValues: { newPassword: "", confirmPassword: "" },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values) => {
      if (!userToResetPassword) return;

      try {
        await userService.resetUserPassword(userToResetPassword._id, values.newPassword);
        enqueueSnackbar(`Password reset successfully for ${userToResetPassword.name}`, { variant: "success" });
        setResetPasswordDialogOpen(false);
        setUserToResetPassword(null);
        resetPasswordFormik.resetForm();
      } catch (error: any) {
        enqueueSnackbar(error.message || "Failed to reset password", { variant: "error" });
      }
    },
  });

  // Load centers - only on initial mount
  const loadCenters = useCallback(async () => {
    if (authLoading || (!user?.isSystemAdmin && user?.roleId?.name !== UserRole.HEAD_OF_ORGANIZATION)) {
      return;
    }

    try {
      const data = await centerService.getCenters();
      setCenters(data?.centers || []);
    } catch (error) {
      console.error("Failed to load centers:", error);
    }
  }, [authLoading, user]);

  // Load roles. For super-admins the schoolId arg scopes the result; school
  // admins are auto-scoped on the server. Used by both the list's Role filter
  // (driven by the School filter) and the Edit modal's Role dropdown
  // (driven by the modal's School picker).
  const loadRoles = useCallback(async (schoolId?: string) => {
    if (authLoading) return;

    try {
      if (isSchoolAdmin) {
        // Catalog endpoint — open to any authenticated school user. We use
        // it here even for School Admins so the call shape stays the same
        // (a chip-friendly list, not the full permission tree).
        const data = await roleService.getRolesCatalog();
        // Filter out School Admin role - School Admin users are created during school setup only
        setRoles((data || []).filter((role: any) => role.name !== 'School Admin'));
      } else {
        const data = await userService.getRoles(schoolId || undefined);
        setRoles(data || []);
      }
    } catch (error) {
      console.error("Failed to load roles:", error);
      enqueueSnackbar("Failed to load roles", { variant: "error" });
    }
  }, [authLoading, isSchoolAdmin, enqueueSnackbar]);

  // Load users - uses debouncedSearchQuery so input doesn't lose focus
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      
      if (isSchoolAdmin) {
        data = await roleService.getUsers({
          page: page + 1,
          limit: rowsPerPage,
          ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
          ...(roleFilter && { roleId: roleFilter }),
          ...(statusFilter && { isActive: statusFilter === "active" }),
        });
      } else {
        data = await userService.getUsers({
          page: page + 1,
          limit: rowsPerPage,
          ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
          ...(roleFilter && { role: roleFilter }),
          ...(schoolFilter && { centerId: schoolFilter }),
          ...(statusFilter && { isActive: statusFilter === "active" }),
        });
      }
      
      const loadedUsers = data?.users || [];
      setUsers(loadedUsers);
      setTotalCount(data?.pagination?.totalItems || 0);
    } catch (error: any) {
      console.error("Failed to load users:", error);
      enqueueSnackbar("Failed to load employees", { variant: "error" });
    } finally {
      setLoading(false);
      if (initialLoad) {
        setInitialLoad(false);
      }
    }
  }, [page, rowsPerPage, debouncedSearchQuery, roleFilter, schoolFilter, statusFilter, isSchoolAdmin, enqueueSnackbar]);

  // Initial data load - runs ONCE on mount
  useEffect(() => {
    loadCenters();
    loadRoles();
  }, [loadCenters, loadRoles]);

  // Super-admin only: when the school filter changes, re-fetch the role list
  // so the Role dropdown only shows that school's roles, and clear any stale
  // role-filter selection that no longer belongs to the new school.
  useEffect(() => {
    if (isSchoolAdmin) return;
    loadRoles(schoolFilter || undefined);
    setRoleFilter('');
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolFilter]);

  // Load users when dependencies change
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Dialog open handler — identity only. Teacher subject wiring is managed
  // in the Teaching Loads module, never from the employee form.
  const handleDialogOpen = useCallback((userToEdit?: User) => {
    setAnchorEl(null);
    setSelectedUser(null);

    if (userToEdit) {
      setEditingUser(userToEdit);
      const roleId = typeof userToEdit.roleId === 'object' ? userToEdit.roleId._id : "";
      const userSchoolId = userToEdit.schoolId && typeof userToEdit.schoolId === "object"
        ? userToEdit.schoolId._id
        : "";
      // Super-admin editing: pre-load that school's roles so the Role
      // dropdown shows real choices for the user's school, not stale ones.
      if (!isSchoolAdmin && userSchoolId) {
        loadRoles(userSchoolId);
      }

      formik.setValues({
        name: userToEdit.name,
        password: "",
        role: (userToEdit.roleId?.name as UserRole) || UserRole.ADMINISTRATOR,
        roleId: roleId,
        centerId: userSchoolId,
      });
    } else {
      setEditingUser(null);
      formik.setValues({
        name: "",
        email: "",
        password: "",
        role: isSchoolAdmin ? "" : UserRole.ADMINISTRATOR,
        roleId: "",
        centerId: "",
      });
    }
    setDialogOpen(true);
  }, [isSchoolAdmin, formik, loadRoles]);

  // Dialog close handler
  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingUser(null);
    formik.resetForm();
    setAnchorEl(null);
    setSelectedUser(null);
    // Super-admin: restore the list's role list to whatever the school filter
    // implies, so the Role filter dropdown isn't left scoped to the modal's
    // previous school selection.
    if (!isSchoolAdmin) {
      loadRoles(schoolFilter || undefined);
    }
  }, [formik, isSchoolAdmin, loadRoles, schoolFilter]);

  // Menu handlers
  const handleMenuClick = useCallback((event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedUser(null);
  }, []);

  // Open confirmation dialog before toggling status
  const handleToggleStatus = useCallback(() => {
    if (!selectedUser) return;
    setUserToToggleStatus(selectedUser);
    setStatusDialogOpen(true);
    handleMenuClose();
  }, [selectedUser, handleMenuClose]);

  // Perform the actual status update after confirmation
  const handleStatusConfirm = useCallback(async () => {
    if (!userToToggleStatus) return;

    setStatusLoading(true);
    try {
      await userService.updateUserStatus(userToToggleStatus._id, !userToToggleStatus.isActive);
      enqueueSnackbar(
        `Employee ${userToToggleStatus.isActive ? "deactivated" : "activated"} successfully`,
        { variant: "success" }
      );
      setStatusDialogOpen(false);
      setUserToToggleStatus(null);
      loadUsers();
    } catch (error) {
      enqueueSnackbar("Failed to update employee status", { variant: "error" });
    } finally {
      setStatusLoading(false);
    }
  }, [userToToggleStatus, loadUsers, enqueueSnackbar]);

  // Reset password handler
  const handleResetPassword = useCallback(() => {
    if (!selectedUser) return;
    setUserToResetPassword(selectedUser);
    setResetPasswordDialogOpen(true);
    handleMenuClose();
  }, [selectedUser, handleMenuClose]);

  // Delete handlers
  const handleDeleteClick = useCallback(() => {
    if (!selectedUser) return;
    setUserToDelete(selectedUser);
    setDeleteDialogOpen(true);
    handleMenuClose();
  }, [selectedUser, handleMenuClose]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!userToDelete) return;

    setDeleteLoading(true);
    try {
      await userService.deleteUser(userToDelete._id);
      enqueueSnackbar("Employee deleted successfully", { variant: "success" });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      enqueueSnackbar("Failed to delete employee", { variant: "error" });
    } finally {
      setDeleteLoading(false);
    }
  }, [userToDelete, loadUsers, enqueueSnackbar]);

  // Get available roles for super admin
  const getAvailableRoles = useCallback((): UserRole[] => {
    if (!user) return [];

    if (user.isSystemAdmin) {
      return Object.values(UserRole);
    }

    switch (user.roleId?.name) {
      case UserRole.HEAD_OF_ORGANIZATION:
        return Object.values(UserRole).filter(role => role !== UserRole.SUPER_ADMIN);
      case UserRole.CHIEF_OPERATING_OFFICER:
        return [
          UserRole.CAMPUS_INCHARGE,
          UserRole.ADMINISTRATOR,
          UserRole.ACADEMICS_INCHARGE,
          UserRole.EXAMINATION_BOARD_HEAD,
        ];
      default:
        return [];
    }
  }, [user]);

  // Get role display name
  const getRoleDisplayName = useCallback((role: UserRole) => {
    const names: Record<string, string> = {
      [UserRole.SUPER_ADMIN]: "Super Admin",
      [UserRole.HEAD_OF_ORGANIZATION]: "Head of Organization",
      [UserRole.CHIEF_OPERATING_OFFICER]: "Chief Operating Officer",
      [UserRole.CAMPUS_INCHARGE]: "Campus Incharge",
      [UserRole.ADMINISTRATOR]: "Administrator",
      [UserRole.ACADEMICS_INCHARGE]: "Academics Incharge",
      [UserRole.EXAMINATION_BOARD_HEAD]: "Examination Board Head",
    };
    return names[role] || role;
  }, []);

  // Permission check
  if (!canRead('users')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            maxWidth: 420,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Block sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight="600" gutterBottom color="text.primary">
            Access Denied
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You don't have permission to view Employees.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Contact your administrator for access.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Loading skeleton - ONLY show on initial load, not during searches
  if (initialLoad && loading) {
    return (
      <Box>
        <Skeleton variant="text" width={250} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Employees Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your organization's employees and their roles
          </Typography>
        </Box>
        {canWrite('users') && (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<GroupAdd />}
              onClick={() => setBulkDialogOpen(true)}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Bulk Upload
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleDialogOpen()}
              sx={{ borderRadius: 2 }}
            >
              Add Employee
            </Button>
          </Box>
        )}
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: user?.isSystemAdmin ? 4 : 5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          {/* Super-admin only: scope to a school. */}
          {user?.isSystemAdmin && (
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>School</InputLabel>
                <Select
                  value={schoolFilter}
                  onChange={(e) => setSchoolFilter(e.target.value)}
                  label="School"
                >
                  <MenuItem value="">All Schools</MenuItem>
                  {centers.map((c) => (
                    <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: user?.isSystemAdmin ? 3 : 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPage(0);
                }}
                label="Role"
              >
                <MenuItem value="">All Roles</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role._id} value={role._id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: user?.isSystemAdmin ? 2 : 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Employees Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          position: 'relative',
        }}
      >
        <TableContainer sx={{ position: 'relative' }}>
          {/* Shimmer scoped to the rows area only — `top: 53` skips the table
              header row, so the column titles never animate. */}
          {loading && !initialLoad && (
            <Box
              sx={{
                position: 'absolute', top: 53, left: 0, right: 0, bottom: 0,
                overflow: 'hidden', pointerEvents: 'none', zIndex: 2,
                '&::before': {
                  content: '""',
                  position: 'absolute', top: 0, bottom: 0, left: 0,
                  width: '40%',
                  background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.10)}, transparent)`,
                  animation: `${tableShimmerKf} 1.4s infinite linear`
                }
              }}
            />
          )}
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email / User ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                    <WorkOutline sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {searchQuery || roleFilter || statusFilter
                        ? 'No employees match your filters'
                        : 'No employees found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((employee) => {
                  const roleName = employee.roleId?.name || "Staff";
                  const roleConfig = getRoleColor(roleName, theme.palette.mode === 'dark');
                  
                  return (
                    <TableRow key={employee._id} hover>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: roleConfig.bgColor,
                              color: roleConfig.color,
                              fontWeight: 600,
                              fontSize: "0.875rem",
                            }}
                          >
                            {employee.name?.charAt(0).toUpperCase() || "?"}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {employee.name}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            fontFamily: employee.userCode && !employee.email ? 'monospace' : 'inherit',
                            fontWeight: employee.userCode && !employee.email ? 500 : 400,
                          }}
                        >
                          {employee.email || employee.userCode || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={roleName}
                          size="small"
                          sx={{
                            bgcolor: roleConfig.bgColor,
                            color: roleConfig.color,
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={employee.isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <Warning sx={{ fontSize: 14 }} />}
                          label={employee.isActive ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            bgcolor: employee.isActive 
                              ? alpha(theme.palette.success.main, 0.1)
                              : alpha(theme.palette.error.main, 0.1),
                            color: employee.isActive 
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            "& .MuiChip-icon": { 
                              color: employee.isActive 
                                ? theme.palette.success.main
                                : theme.palette.error.main 
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(employee.createdAt), "MMM dd, yyyy")}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="More Actions">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, employee)}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 180,
            mt: 0.5,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
              gap: 1.5,
            },
          },
        }}
      >
        <MenuItem onClick={() => handleDialogOpen(selectedUser!)}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Edit fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText
            primary={selectedIsSchoolAdmin ? 'Edit name' : 'Edit'}
            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
          />
        </MenuItem>
        {/* Deactivate / Reset Password / Delete are hidden for the School
            Admin (owner) row — the BE enforces the same rules on all three
            endpoints, this just keeps the UI honest. */}
        {!selectedIsSchoolAdmin && (
          <MenuItem onClick={handleToggleStatus}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              {selectedUser?.isActive ? <Lock fontSize="small" color="warning" /> : <LockOpen fontSize="small" color="success" />}
            </ListItemIcon>
            <ListItemText
              primary={selectedUser?.isActive ? "Deactivate" : "Activate"}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
            />
          </MenuItem>
        )}
        {!selectedIsSchoolAdmin && (
          <MenuItem onClick={handleResetPassword}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Lock fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText
              primary="Reset Password"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
            />
          </MenuItem>
        )}
        {!selectedIsSchoolAdmin && <Divider sx={{ my: 0.5 }} />}
        {!selectedIsSchoolAdmin && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText
              primary="Delete"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
            />
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Employee Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                <BadgeOutlined sx={{ color: theme.palette.primary.main }} />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {editingUser ? "Edit Employee" : "Add New Employee"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {editingUser ? "Update employee details" : "Create a new employee account"}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleDialogClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        
        <form onSubmit={formik.handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            {/* Show existing User ID for users who have one */}
            {editingUser?.userCode && (
              <Alert 
                severity="info" 
                sx={{ mb: 2.5, borderRadius: 2 }}
                icon={<BadgeOutlined />}
              >
                <Typography variant="body2">
                  <strong>User ID:</strong>{' '}
                  <code style={{ 
                    fontFamily: 'monospace', 
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    padding: '2px 6px',
                    borderRadius: 4,
                    fontWeight: 600,
                  }}>
                    {editingUser.userCode}
                  </code>
                  {' '}— This employee logs in using this User ID.
                </Typography>
              </Alert>
            )}

            {/* Basic Information */}
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="name"
                  label="Full Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && (formik.errors.name as string)}
                  placeholder="Enter employee's full name"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="userCode"
                  label="User ID"
                  value={
                    editingUser 
                      ? (editingUser.userCode || '—')
                      : (user?.schoolId?.code ? `${user.schoolId.code}-XXXXXX` : 'XXXX-XXXXXX')
                  }
                  disabled
                  helperText={
                    editingUser 
                      ? "User ID assigned to this employee for login"
                      : "Unique ID will be auto-generated for employee login (e.g., ABC-000001)"
                  }
                  InputProps={{
                    style: { fontFamily: 'monospace', fontWeight: 600 }
                  }}
                />
              </Grid>

              {!editingUser && (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && (formik.errors.password as string)}
                    placeholder="Minimum 6 characters"
                  />
                </Grid>
              )}

              {editingIsSchoolAdmin && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    This is the <strong>School Admin</strong> account. Only the name can be edited here — email, role, and active state are locked.
                  </Alert>
                </Grid>
              )}

              {/* School picker (super-admin only) — sits ABOVE Role so the
                  Role dropdown can be populated for the selected school. */}
              {user?.isSystemAdmin && (
                <Grid size={{ xs: 12, sm: editingUser ? 12 : 6 }}>
                  <FormControl fullWidth disabled={editingIsSchoolAdmin}>
                    <InputLabel>School</InputLabel>
                    <Select
                      name="centerId"
                      value={formik.values.centerId}
                      onChange={(e) => {
                        formik.handleChange(e);
                        // Clear the role choice — it belonged to the prior
                        // school and may not exist in the new school's set.
                        formik.setFieldValue('roleId', '');
                        loadRoles(e.target.value as string || undefined);
                      }}
                      error={formik.touched.centerId && Boolean(formik.errors.centerId)}
                      label="School"
                    >
                      {centers.map((center) => (
                        <MenuItem key={center._id} value={center._id}>
                          {center.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid size={{ xs: 12, sm: editingUser ? 12 : 6 }}>
                <FormControl
                  fullWidth
                  disabled={editingIsSchoolAdmin || (user?.isSystemAdmin && !formik.values.centerId)}
                >
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="roleId"
                    value={formik.values.roleId}
                    onChange={formik.handleChange}
                    error={formik.touched.roleId && Boolean(formik.errors.roleId)}
                    label="Role"
                  >
                    {roles.length === 0 && (
                      <MenuItem value="" disabled>
                        <em>{user?.isSystemAdmin && !formik.values.centerId ? 'Select a school first' : 'No roles available'}</em>
                      </MenuItem>
                    )}
                    {roles.map((role) => {
                      const config = getRoleColor(role.name, theme.palette.mode === 'dark');
                      return (
                        <MenuItem key={role._id} value={role._id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: config.color }}
                            />
                            <Typography variant="body2">{role.name}</Typography>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>

          <Divider />
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button type="button" onClick={handleDialogClose} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
            >
              {formik.isSubmitting ? 'Saving...' : (editingUser ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => {
          setResetPasswordDialogOpen(false);
          setUserToResetPassword(null);
          resetPasswordFormik.resetForm();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
              <Lock sx={{ color: theme.palette.warning.main }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Reset Password
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {userToResetPassword?.name} ({userToResetPassword?.email || userToResetPassword?.userCode})
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />
        <form onSubmit={resetPasswordFormik.handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              The employee will need to use this new password to login.
            </Alert>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="newPassword"
                  label="New Password"
                  type="password"
                  value={resetPasswordFormik.values.newPassword}
                  onChange={resetPasswordFormik.handleChange}
                  error={resetPasswordFormik.touched.newPassword && Boolean(resetPasswordFormik.errors.newPassword)}
                  helperText={resetPasswordFormik.touched.newPassword && resetPasswordFormik.errors.newPassword}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  name="confirmPassword"
                  label="Confirm New Password"
                  type="password"
                  value={resetPasswordFormik.values.confirmPassword}
                  onChange={resetPasswordFormik.handleChange}
                  error={resetPasswordFormik.touched.confirmPassword && Boolean(resetPasswordFormik.errors.confirmPassword)}
                  helperText={resetPasswordFormik.touched.confirmPassword && resetPasswordFormik.errors.confirmPassword}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button 
              onClick={() => {
                setResetPasswordDialogOpen(false);
                setUserToResetPassword(null);
                resetPasswordFormik.resetForm();
              }}
              color="inherit"
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="warning">
              Reset Password
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Bulk Upload Employees Dialog */}
      <BulkUploadEmployeesDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        onSuccess={() => loadUsers()}
        roles={roles}
      />

      {/* Deactivate / Activate Confirmation Dialog */}
      <ConfirmationDialog
        open={statusDialogOpen}
        onClose={() => {
          setStatusDialogOpen(false);
          setUserToToggleStatus(null);
        }}
        onConfirm={handleStatusConfirm}
        title={userToToggleStatus?.isActive ? "Deactivate Employee?" : "Activate Employee?"}
        message={
          userToToggleStatus?.isActive
            ? `Deactivating "${userToToggleStatus?.name}" will prevent them from logging into the system. Their data will remain intact and can be restored at any time.`
            : `Activating "${userToToggleStatus?.name}" will restore their access to the system.`
        }
        confirmText={userToToggleStatus?.isActive ? "Deactivate" : "Activate"}
        cancelText="Cancel"
        confirmColor={userToToggleStatus?.isActive ? "warning" : "success"}
        loading={statusLoading}
      >
        {userToToggleStatus && (
          <Card variant="outlined" sx={{ mt: 2, bgcolor: 'action.hover' }}>
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2" fontWeight={500}>{userToToggleStatus.name}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Email / User ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: userToToggleStatus.userCode && !userToToggleStatus.email ? 'monospace' : 'inherit' }}>
                    {userToToggleStatus.email || userToToggleStatus.userCode || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Role</Typography>
                  <Typography variant="body2">{userToToggleStatus.roleId?.name || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Current Status</Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color={userToToggleStatus.isActive ? 'success.main' : 'error.main'}
                  >
                    {userToToggleStatus.isActive ? 'Active' : 'Inactive'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </ConfirmationDialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Employee?"
        message={`Are you sure you want to delete "${userToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        loading={deleteLoading}
      >
        {userToDelete && (
          <Card variant="outlined" sx={{ mt: 2, bgcolor: 'action.hover' }}>
            <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2" fontWeight={500}>{userToDelete.name}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Email / User ID</Typography>
                  <Typography variant="body2" sx={{ fontFamily: userToDelete.userCode && !userToDelete.email ? 'monospace' : 'inherit' }}>
                    {userToDelete.email || userToDelete.userCode || 'N/A'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Role</Typography>
                  <Typography variant="body2">{userToDelete.roleId?.name || 'N/A'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body2">{userToDelete.isActive ? 'Active' : 'Inactive'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}
      </ConfirmationDialog>
    </Box>
  );
};

export default EmployeesPage;
